import { useForm } from '@nerdgeschoss/react-use-form-library';
import React, { useEffect } from 'react';
import { FormForm } from '../../../../components/Form/FormForm';
import { FormNumberField } from '../../../../components/Form/NumberField/FormNumberField';
import { Modal } from '../../../../components/Modal/Modal';
import { Stack } from '../../../../components/Stack/Stack';
import { graphql } from '../../../../generated';
import { t } from '../../../../locales/translate';
import { useMutationHandler } from '../../../../model/graphql';
import { handleSuccess } from '../../../../model/notification';
import './RefundPaymentModal.css';

const REFUND_PAYMENT = graphql(`
  mutation RefundPayment($amount: Float!, $paymentId: ID!) {
    refundPayment(input: { amount: $amount, paymentId: $paymentId }) {
      payment {
        id
        amountRemaining
      }
    }
  }
`);

function useLogRefundPayment(): (orderReference: string, refundValue: number) => void {
  useEffect(() => {
    const script = document.createElement('script');

    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-Q1M9T7TTKY';
    script.async = true;

    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (orderReference, refundValue): void => {
    type windowWithGtag = typeof window & { dataLayer: unknown[] };
    const dataLayer = (window as windowWithGtag).dataLayer || [];
    function gtag(...params: unknown[]): void {
      dataLayer.push(params);
    }

    const hasConfig = dataLayer.some((a) => Array.isArray(a) && a.includes('config'));
    if (!hasConfig) {
      gtag('config', 'G-Q1M9T7TTKY');
    }

    gtag('event', 'refund', {
      currency: 'EUR',
      transaction_id: { order_reference: orderReference },
      value: refundValue,
    });
  };
}

interface Props {
  payment: {
    id: string;
    amountRemaining: number;
    orderBalance: number;
    orderReference: string;
  };
  onClose: (success: boolean) => void;
}

export function RefundPaymentModal({ payment, onClose }: Props): JSX.Element {
  const logRefund = useLogRefundPayment();
  const repayableBalance = -payment.orderBalance;
  const [refundPayment] = useMutationHandler(REFUND_PAYMENT);
  const { model, valid, fields, onSubmit, submissionStatus, updateFields } = useForm<{
    amount: number | undefined | null;
  }>({
    model: {
      amount: repayableBalance,
    },
    validations: {
      amount: ({ model }) => {
        if (!model.amount || model.amount <= 0 || model.amount > payment.amountRemaining) {
          return [t.refund.maxAmount(payment.amountRemaining)];
        }
        return [];
      },
    },
    onSubmit: async () => {
      if (!payment) return;

      await refundPayment({ variables: { paymentId: payment.id, amount: model.amount! } });

      logRefund(payment.orderReference, model.amount!);

      handleSuccess({
        message: t.refund.success,
      });
      onClose(true);
    },
  });

  useEffect(() => {
    updateFields({ amount: repayableBalance ?? 0 });
  }, [repayableBalance]);

  return (
    <Modal open={!!payment} title={t.refund.title} size="small">
      {payment && (
        <FormForm
          onSubmit={onSubmit}
          onClose={() => {
            onClose(false);
          }}
          canSubmit={valid}
          loading={submissionStatus === 'submitting'}
          saveButtonTitle={t.refund.refund}
        >
          <Stack>
            <p>
              <span className="refund-payment-modal__description--bold">{t.refund.attention}</span>{' '}
              {t.refund.description}
            </p>
            <FormNumberField
              {...fields.amount}
              label={t.refund.amount}
              required
              step={0.01}
              min={0.01}
              max={payment.amountRemaining}
            />
          </Stack>
        </FormForm>
      )}
    </Modal>
  );
}
